import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, GridLegacy as Grid, Stack, Typography } from '@mui/material';

import { AccommodationPropertyDetailsContext } from '~/components/Accommodation/Pages/PropertyDetails/context/AccommodationPropertyDetailsContext';
import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_COORDINATOR, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';

import useToggleState from '~/hooks/useToggleState';

import {
  AdminPrompt,
  getPropertyContent,
  getPropertyPrompt,
  initiatePropertyContentGeneration,
} from '~/services/AccommodationService';

import GenerateContentModal, { InitiateContentGenerationRequest } from '../../../../../Common/GenerateContentModal';

import AccommodationGeneratedContentView from './AccommodationGeneratedContentView';

export default function AccommodationPropertyDetailsSection() {
  const { enqueueSnackbar } = useSnackbar();
  const { propertyDetails, propertyContent } = useAccommodationPropertyDetailsContext();
  const [propertyPrompt, setPropertyPrompt] = useState<AdminPrompt | null>(null);
  const { setPropertyContent } = useContext(AccommodationPropertyDetailsContext);
  const [useWebSearch, setUseWebSearch] = useState(false);

  useEffect(() => {
    const fetchPrompt = async () => {
      const data = await getPropertyPrompt();
      setPropertyPrompt({
        template: (data.result?.templates?.write as string) || '',
        examples: data.result?.examples || [],
        variables: {
          abstain: data.result?.variables?.abstain || '',
          voice: data.result?.variables?.voice || '',
        },
      });
    };
    fetchPrompt();
  }, []);

  const {
    isToggled: isGenerateContentModalOpen,
    toggleOn: openGenerateContentModal,
    toggleOff: closeGenerateContentModal,
  } = useToggleState(false);

  const hasGeneratedContent = useMemo(() => propertyContent?.generatedContents.length > 0, [propertyContent]);

  const fetchContent = async () => {
    try {
      const response = await getPropertyContent({ propertyId: propertyDetails.id });
      if (response.result && setPropertyContent) {
        setPropertyContent(response.result);
      } else {
        enqueueSnackbar(`Failed to fetch property content: ${JSON.stringify(response)}`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to fetch property content: ${error.message}`, { variant: 'error' });
    }
  };

  const initiateContentGeneration = (params: InitiateContentGenerationRequest) => {
    return initiatePropertyContentGeneration({
      propertyId: propertyDetails.id,
      ...params,
    });
  };

  const CONTENT_ROLES = [ROLE_ADMIN_USER, ROLE_COORDINATOR, ROLE_HOTEL_COORDINATOR];

  return (
    <Grid container spacing={4}>
      <Grid item xs={3}>
        <Stack direction="column" gap={4}>
          <Box>
            <DefinitionTitle>Country</DefinitionTitle>
            <DefinitionText>{propertyDetails.country || 'Unknown'}</DefinitionText>
          </Box>
          <Box>
            <DefinitionTitle>City</DefinitionTitle>
            <DefinitionText>{propertyDetails.city || 'Unknown'}</DefinitionText>
          </Box>
          <Box>
            <DefinitionTitle>Rating</DefinitionTitle>
            <DefinitionText>{propertyDetails.rating}</DefinitionText>
          </Box>
        </Stack>
      </Grid>
      <Grid item xs={9}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" align="center">
            {hasGeneratedContent ? 'Generated Content' : 'No generated content'}
          </Typography>
          <PermissionedComponent requiredRoles={CONTENT_ROLES}>
            <Button variant="contained" color="primary" onClick={openGenerateContentModal}>
              {hasGeneratedContent ? 'Regenerate Content' : 'Generate Content'}
            </Button>
          </PermissionedComponent>
        </Stack>
        {hasGeneratedContent && (
          <PermissionedComponent requiredRoles={CONTENT_ROLES}>
            <AccommodationGeneratedContentView
              content={propertyContent.generatedContents}
              propertyPrompt={propertyPrompt}
              setPropertyPrompt={setPropertyPrompt}
            />
          </PermissionedComponent>
        )}
      </Grid>

      {isGenerateContentModalOpen && (
        <GenerateContentModal
          onClose={closeGenerateContentModal}
          prompt={propertyPrompt}
          setPrompt={setPropertyPrompt}
          initiateContentGeneration={initiateContentGeneration}
          fetchContent={fetchContent}
          setUseWebSearch={setUseWebSearch}
          useWebSearch={useWebSearch}
        />
      )}
    </Grid>
  );
}
